import "./EnergyField.css";

import { useEffect, useMemo, useRef } from "react";

import { handles, prefersReducedMotion,useLoopInView } from "@/4-components/animations/anime";
import { watchRect } from "@/4-components/animations/pointer";
import { useViewport } from "@/3-features/context/ViewportContext";
import { GREEN } from "@/5-shared/constants/palette";

import { Clock } from "./Clock";
import { clamp01, driftX, driftY, FIELD, frameAt, type Layer, N, type Painter, type Pt,R_DOT, repel, Strand, toPath, twinkle } from "./core";
import { Ebitda } from "./Ebitda";
import { Reduction } from "./Reduction";
import { Snake } from "./Snake";

/** One animation per stat, in the order the numbers are shown. */
const COMPANY_LAYERS: Layer[] = [Reduction, Ebitda, Clock, Snake];

/** How fast an animation fades in/out when the selection changes (per frame). */
const FADE = 0.09;
/** Below this presence an animation is treated as gone. */
const EPS = 0.01;
/** Only close a loop once it has settled, avoids a chord flashing mid-morph. */
const CLOSE_AT = 0.8;
/** Morph timing, seconds: each point eases over MORPH, delayed out from the centre. */
const MORPH = 1.4;
const STAGGER = 0.007;
const MID = (N - 1) / 2;
const easeOut = (p: number) => 1 - (1 - p) ** 4;

type Mouse = { x: number; y: number; on: boolean };

/**
 * Follow the cursor in the svg's own user-space (it is fitted with `meet`).
 *
 * The listener is on `window`, so it fires for movement anywhere on the page: the
 * box comes from `watchRect` (measured once, re-measured on resize/scroll rather
 * than per event, never inside the handler) and the maths is deferred to a frame.
 * The un-projection undoes `preserveAspectRatio="xMidYMid meet"`.
 */
function trackPointer(svg: SVGSVGElement, mouse: Mouse): () => void {
  const rect = watchRect(svg);
  let raf = 0;
  let ev: PointerEvent | null = null;

  const apply = () => {
    raf = 0;
    const r = rect.read();
    if (!ev || !r.width || !r.height) return;
    const scale = Math.min(r.width / FIELD.w, r.height / FIELD.h);
    mouse.x = (ev.clientX - r.left - (r.width - FIELD.w * scale) / 2) / scale;
    mouse.y = (ev.clientY - r.top - (r.height - FIELD.h * scale) / 2) / scale;
    mouse.on = true;
  };
  const onMove = (e: PointerEvent) => {
    ev = e;
    if (!raf) raf = requestAnimationFrame(apply);
  };
  const onLeave = () => (mouse.on = false);

  rect.measure();
  window.addEventListener("pointermove", onMove, { passive: true });
  document.addEventListener("pointerleave", onLeave);
  return () => {
    if (raf) cancelAnimationFrame(raf);
    window.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerleave", onLeave);
    rect.dispose();
    mouse.on = false;
  };
}

export function EnergyField({ active, hot, layers = COMPANY_LAYERS }: { active: number; hot: boolean; layers?: Layer[] }) {
  /**
   * Resting positions per animation. Loops space points `i/N`, no seam duplicate.
   * Built here rather than at module scope: this is one layer set × N points of
   * trigonometry, and the module is pulled into the shared bundle, so at module
   * scope it would run on every route (and on the server) whether or not the
   * counter is on the page.
   */
  const shapes = useMemo<Pt[][]>(() => layers.map((L) => Array.from({ length: N }, (_, i) => L.shape(L.closed ? i / N : i / (N - 1)))), [layers]);

  const paint = useRef<((t: number) => void) | null>(null);
  const painters = useRef<Painter[]>([]);
  const register = useMemo(() => layers.map((_, k) => (p: Painter) => void (painters.current[k] = p)), [layers]);
  // morph state: points ease from `origin` to `target`, staggered out of the centre
  const rest = useRef(shapes[0].map((p) => ({ ...p })));
  const origin = useRef(shapes[0].map((p) => ({ ...p })));
  const target = useRef(shapes[0]);
  const startedAt = useRef(0);
  const clock = useRef(0);
  const presence = useRef(layers.map((_, k) => (k === 0 ? 1 : 0)));
  const mouse = useRef<Mouse>({ x: 0, y: 0, on: false });
  const selected = useRef(active);

  useEffect(() => {
    selected.current = active;
  }, [active]);

  const ref = useLoopInView<HTMLDivElement>(
    (root) => {
      const { one, all } = handles(root);
      const svg = one<SVGSVGElement>("svg");
      const dots = all<SVGCircleElement>(".field-dot");
      const strand = one<SVGPathElement>(".field-strand");
      const glow = one<SVGPathElement>(".field-glow");
      if (!svg || dots.length === 0) return;

      const still = prefersReducedMotion();
      // bound only while the field is actually on screen and running
      let untrack: (() => void) | null = null;
      const pts: Pt[] = dots.map(() => ({ x: 0, y: 0 }));
      const showing = layers.map(() => false);

      const render = (t: number) => {
        clock.current = t;
        const f = frameAt(t);
        const act = selected.current;
        const floorY = layers[act].floorY;
        const dotsOn = layers[act].dots !== false;
        const shown = presence.current;
        for (let k = 0; k < shown.length; k++) {
          shown[k] += ((k === act ? 1 : 0) - shown[k]) * (still ? 1 : FADE);
        }

        for (let i = 0; i < N; i++) {
          const u = i / (N - 1);
          // morph: ease this point from where it was toward its new shape
          const base = rest.current[i];
          const e = still ? 1 : easeOut(clamp01((t - startedAt.current - STAGGER * Math.abs(i - MID)) / MORPH));
          base.x = origin.current[i].x + (target.current[i].x - origin.current[i].x) * e;
          base.y = origin.current[i].y + (target.current[i].y - origin.current[i].y) * e;

          const p = pts[i];
          p.x = base.x + driftX(i, t);
          p.y = base.y + driftY(i, t);

          let light = 0;
          let idle = 0; // presence-weighted idle brightness, so fades blend too
          for (let k = 0; k < layers.length; k++) {
            const a = shown[k];
            if (a <= EPS) continue;
            const { offsetY, light: lit, fade = 1 } = layers[k];
            idle += fade * a;
            if (offsetY) p.y += offsetY(u, base.y, f) * a;
            if (lit) light += lit(u, p.x, p.y, f) * a;
          }
          if (mouse.current.on) repel(p, mouse.current.x, mouse.current.y);
          if (floorY !== undefined) p.y = Math.min(p.y, floorY);

          dots[i].setAttribute("cx", p.x.toFixed(1));
          dots[i].setAttribute("cy", p.y.toFixed(1));
          dots[i].setAttribute("opacity", dotsOn ? clamp01(twinkle(i, t) * (idle || 1) + light).toFixed(2) : "0");
        }

        // A layer can opt out of the shared strand (`strand: false`) when it draws its
        // own lines, else the full-width strand cuts a bright line through them.
        const d = layers[act].strand === false ? "" : toPath(pts, layers[act].closed && shown[act] > CLOSE_AT, floorY);
        strand?.setAttribute("d", d);
        glow?.setAttribute("d", d);

        // Layers paint while they show, plus once at zero so they hide themselves.
        for (let k = 0; k < layers.length; k++) {
          const on = shown[k] > EPS;
          if (on || showing[k]) painters.current[k]?.(pts, on ? shown[k] : 0, f);
          showing[k] = on;
        }
      };
      paint.current = render;
      render(0); // first frame, also the one reduced-motion users keep

      let raf = 0;
      const loop = (now: number) => {
        render(now * 0.001);
        raf = requestAnimationFrame(loop);
      };
      const stop = () => {
        cancelAnimationFrame(raf);
        raf = 0;
      };
      const release = () => {
        stop();
        untrack?.();
        untrack = null;
      };
      return {
        play: () => {
          untrack ??= trackPointer(svg, mouse.current);
          if (!raf) raf = requestAnimationFrame(loop);
        },
        pause: release,
        revert: () => {
          release();
          paint.current = null;
        },
      };
    },
    [layers],
  );

  // Start a morph from wherever the points currently are, so selections chain.
  useEffect(() => {
    origin.current = rest.current.map((p) => ({ ...p }));
    target.current = shapes[active];
    startedAt.current = clock.current;
    paint.current?.(clock.current); // covers the paused / reduced-motion case
  }, [active, shapes]);

  // Strand, halo and dots stay brand green regardless of which stat is active.
  const color = GREEN;
  // The 2.5:1 field is far wider than the near-square mobile box. `meet` would fit it
  // whole and shrink it to a thin band, so on mobile fill the box and crop the far ends
  // (the centre carries the morph/step) instead.
  const { isMobile } = useViewport();
  // neither depends on a changing prop, and `hot` changes on every hover, without
  // this, moving the cursor over a stat reconciles 160 circles
  const decorations = useMemo(() => layers.map((L, k) => <L key={k} register={register[k]} />), [layers, register]);
  const seeds = useMemo(
    () => shapes[0].map((p, i) => <circle key={i} className="field-dot" cx={p.x} cy={p.y} r={R_DOT} fill="currentColor" opacity={0.6} />),
    [shapes],
  );

  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-hot={hot ? "" : undefined}
      className="burst-wrap pointer-events-none relative z-0 -mt-2 h-80 w-full overflow-hidden md:mt-0 md:h-auto md:aspect-[5/2]"
      style={{ ["--fc" as string]: color }}
    >
      <span
        className="absolute left-1/2 top-1/2 h-96  max-w-full -translate-x-1/2 -translate-y-1/2 rounded-[50%] blur-3xl transition-colors duration-700"
        style={{
          background: `radial-gradient(50% 50%, ${color}33, transparent 70%)`,
        }}
      />
      <svg
        viewBox={`0 0 ${FIELD.w} ${FIELD.h}`}
        preserveAspectRatio={isMobile ? "xMidYMid slice" : "xMidYMid meet"}
        className="absolute inset-0 h-full w-full"
      >
        <g className="field-group" style={{ color }}>
          {decorations}
          <Strand className="field-glow" width={8} opacity={0.14} />
          <Strand className="field-strand" opacity={0.55} />
          {seeds}
        </g>
      </svg>
    </div>
  );
}
